import { extend } from 'vee-validate'
import { email, max, required } from 'vee-validate/dist/rules'
import { isValidWebhookUrl } from '@/modules/developer/utils/webhookUrl'
import { isValidSignupPassword } from '@/util/password-validation'

// More than one link: any inner whitespace, or a comma that starts another link
const MULTIPLE_LINKS = /\s|,(?:https?:\/\/|www\.)/i

export const initValidation = function () {
  extend('required', {
    ...required,
    message: 'This field is required'
  })

  extend('max', {
    ...max,
    message: 'This field is too long'
  })

  extend('email', {
    ...email,
    message: 'This is not a valid email'
  })

  extend('isValidLinkedinUrl', {
    validate: url => {
      const regex = /https:\/\/www\.linkedin\.com\/in\/([\w-]+|%[0-9A-Fa-f]{2})+/
      return regex.test(url)
    },
    message: 'Enter a valid LinkedIn URL'
  })
  extend('emailplusimpersonation', {
    validate: (value: string | string[], { multiple }: Record<string, any>) => {
      if (typeof (value as any).split === 'function') {
        const parts = (value as string).split(':')
        if (parts.length === 2) {
          return email.validate(parts[0], multiple) && email.validate(parts[1], multiple)
        }
      }
      return email.validate(value, multiple)
    },
    params: email.params,
    message: 'This is not a valid email'
  })

  extend('phone', {
    validate: value => {
      return /^(\+)?[0-9\s]+$/.test(value)
    },
    message: 'This is not a valid phone number'
  })

  extend('url', {
    validate: value => {
      // eslint complains here about unnecessary chars, which are absolutely necessary.
      // eslint-disable-next-line
          return /[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)?/gi.test(value)
    },
    message: 'This is not a valid URL'
  })

  extend('singleUrl', {
    validate: value => {
      // Empty is for `required` to report, not this rule
      return !MULTIPLE_LINKS.test(String(value ?? '').trim())
    },
    message: 'Enter one link'
  })

  extend('httpUrl', {
    validate: value => {
      try {
        const trimmedValue = String(value).trim()
        const candidate = /^[a-z][a-z0-9+.-]*:/i.test(trimmedValue) ? trimmedValue : `https://${trimmedValue}`
        const parsed = new URL(candidate)
        return ['http:', 'https:'].includes(parsed.protocol) && !!parsed.hostname
      } catch {
        return false
      }
    },
    message: 'This is not a valid URL'
  })

  extend('domain', {
    validate: value => {
      return /^(?:\*\.)?(?!-)[A-Za-z0-9-]{1,63}(?<!-)(?:\.[A-Za-z0-9-]{1,63})*\.[A-Za-z]{2,63}$/i.test(value)
    },
    message: 'This is not a valid domain'
  })

  extend('webhookUrl', {
    validate: value => isValidWebhookUrl(value),
    message: 'Enter a valid public HTTPS endpoint URL'
  })

  extend('smtpPort', {
    validate: value => {
      const port = Number(value)
      return Number.isInteger(port) && port >= 1 && port <= 65535
    },
    message: 'Enter a valid port between 1 and 65535'
  })

  extend('signupPassword', {
    validate: value => isValidSignupPassword(String(value ?? '')),
    message: 'Password must be at least 8 characters and include uppercase, lowercase, and a number or symbol'
  })
}
